import {
  formatRupiah,
  NON_CASH_METHODS,
  type NonCashMethod,
  PAYMENT_LABELS,
} from '@safepos/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  type Column,
  DataTable,
  PageHeader,
  SelectField,
  TextField,
} from '@safepos/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { API_ORIGIN, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSubmit } from '../lib/useForm';
import { useInvalidate } from './CatalogPages';

interface Config {
  environment: 'simulator' | 'sandbox' | 'production';
  merchantCode: string | null;
  clientKey: string | null;
  serverKeySet: boolean;
  serverKeyHint: string | null;
  enabledMethods: NonCashMethod[];
  simulatorAllowed: boolean;
}
interface Intent {
  id: string;
  method: NonCashMethod;
  amount: number;
  status: 'pending' | 'success' | 'failed' | 'expired' | 'cancelled';
  environment: string;
  orderId: string | null;
  failReason: string | null;
  createdAt: string;
  providerOrderRef: string;
}
interface Recon {
  checked: number;
  matched: number;
  healed: string[];
  mismatched: { id: string; local: string; midtrans: string }[];
  unreachable: number;
  missingOrder: string[];
}

const tone = {
  pending: 'warning',
  success: 'success',
  failed: 'danger',
  expired: 'neutral',
  cancelled: 'neutral',
} as const;
const statusText = {
  pending: 'Menunggu',
  success: 'Berhasil',
  failed: 'Gagal',
  expired: 'Kedaluwarsa',
  cancelled: 'Dibatalkan',
};

/** Pengaturan Midtrans per tenant + daftar pembayaran non-tunai + rekonsiliasi. */
export function PaymentsPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const write = can('settings:write');
  const cfg = useQuery({
    queryKey: ['pay-config'],
    queryFn: () => api.get<Config>('/payments/config'),
  });
  const [status, setStatus] = useState('');
  const intents = useQuery({
    queryKey: ['pay-intents', status],
    refetchInterval: 8000,
    queryFn: () =>
      api.get<Intent[]>(`/payments/intents?limit=100${status ? `&status=${status}` : ''}`),
  });
  const [form, setForm] = useState<{
    environment: Config['environment'];
    merchantCode: string;
    clientKey: string;
    serverKey: string;
    enabled: Set<NonCashMethod>;
  } | null>(null);
  useEffect(() => {
    if (cfg.data && !form)
      setForm({
        environment: cfg.data.environment,
        merchantCode: cfg.data.merchantCode ?? '',
        clientKey: cfg.data.clientKey ?? '',
        serverKey: '',
        enabled: new Set(cfg.data.enabledMethods),
      });
  }, [cfg.data, form]);

  const [notice, setNotice] = useState<string | null>(null);
  const save = useSubmit(async () => {
    const f = form!;
    await api.put('/payments/config', {
      environment: f.environment,
      merchantCode: f.merchantCode.trim() || null,
      clientKey: f.clientKey.trim() || null,
      ...(f.serverKey.trim() ? { serverKey: f.serverKey.trim() } : {}),
      enabledMethods: NON_CASH_METHODS.filter((m) => f.enabled.has(m)),
    });
    setForm({ ...f, serverKey: '' });
    setNotice('Pengaturan disimpan');
    inv('pay-config');
  });
  const test = useSubmit(async () => {
    const r = await api.post<{ ok: boolean; message: string }>('/payments/config/test', {});
    setNotice(`${r.ok ? '✓' : '✗'} ${r.message}`);
  });
  const act = useSubmit(async (path: string, body: unknown = {}) => {
    await api.post(path, body);
    inv('pay-intents');
  });
  const [recon, setRecon] = useState<Recon | null>(null);
  const reconcile = useSubmit(async () => {
    setRecon(await api.post<Recon>('/payments/reconcile', { hours: 24 }));
    inv('pay-intents');
  });

  const cols: Column<Intent>[] = [
    {
      header: 'Waktu',
      cell: (i) =>
        new Date(i.createdAt).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' }),
    },
    { header: 'Metode', cell: (i) => PAYMENT_LABELS[i.method] },
    { header: 'Jumlah', cell: (i) => formatRupiah(i.amount) },
    {
      header: 'Status',
      cell: (i) => (
        <span>
          <Badge tone={tone[i.status]}>{statusText[i.status]}</Badge>
          {i.environment === 'simulator' && (
            <span className="ml-1 text-xs text-slate-500">simulator</span>
          )}
          {i.status === 'success' && !i.orderId && (
            <span className="ml-1 text-xs text-red-600">order belum dibuat</span>
          )}
        </span>
      ),
    },
    {
      header: 'Ref',
      cell: (i) => <span className="font-mono text-xs">{i.providerOrderRef.slice(0, 16)}…</span>,
    },
    {
      header: '',
      className: 'text-right',
      cell: (i) => (
        <div className="flex justify-end gap-2">
          {i.environment === 'simulator' && i.status === 'pending' && write && (
            <>
              <Button
                onClick={() =>
                  void act.run(`/payments/intents/${i.id}/simulate`, { status: 'success' })
                }
              >
                Setujui (simulasi)
              </Button>
              <Button
                variant="ghost"
                onClick={() =>
                  void act.run(`/payments/intents/${i.id}/simulate`, { status: 'failed' })
                }
              >
                Gagalkan
              </Button>
            </>
          )}
          {i.environment !== 'simulator' &&
            (i.status === 'pending' || (i.status === 'success' && !i.orderId)) && (
              <Button
                variant="secondary"
                onClick={() => void act.run(`/payments/intents/${i.id}/refresh`)}
              >
                Cek ulang
              </Button>
            )}
        </div>
      ),
    },
  ];

  const webhook = `${API_ORIGIN}/v1/webhooks/midtrans`;
  return (
    <>
      <PageHeader
        title="Pembayaran Non-Tunai"
        subtitle="QRIS, GoPay, dan Virtual Account via Midtrans (Core API)"
      />
      {notice && <Alert tone="info">{notice}</Alert>}
      {(save.error || test.error || act.error || reconcile.error) && (
        <Alert>{save.error ?? test.error ?? act.error ?? reconcile.error}</Alert>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Pengaturan Midtrans">
          {form && (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void save.run();
              }}
            >
              <SelectField
                label="Lingkungan"
                value={form.environment}
                disabled={!write}
                onChange={(e) =>
                  setForm({ ...form, environment: e.target.value as Config['environment'] })
                }
              >
                {cfg.data?.simulatorAllowed && (
                  <option value="simulator">Simulator (uji tanpa Midtrans)</option>
                )}
                <option value="sandbox">Sandbox Midtrans</option>
                <option value="production">Produksi</option>
              </SelectField>
              {form.environment !== 'simulator' && (
                <>
                  <TextField
                    label="Merchant ID"
                    value={form.merchantCode}
                    disabled={!write}
                    onChange={(e) => setForm({ ...form, merchantCode: e.target.value })}
                  />
                  <TextField
                    label="Client key"
                    value={form.clientKey}
                    disabled={!write}
                    onChange={(e) => setForm({ ...form, clientKey: e.target.value })}
                  />
                  <TextField
                    label={`Server key${cfg.data?.serverKeySet ? ` (tersimpan •••• ${cfg.data.serverKeyHint}; kosongkan bila tidak diganti)` : ''}`}
                    type="password"
                    autoComplete="off"
                    value={form.serverKey}
                    disabled={!write}
                    onChange={(e) => setForm({ ...form, serverKey: e.target.value })}
                  />
                </>
              )}
              <fieldset>
                <legend className="mb-1 text-sm font-medium">Metode aktif di kasir</legend>
                <div className="grid grid-cols-2 gap-2">
                  {NON_CASH_METHODS.map((m) => (
                    <label key={m} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        disabled={!write}
                        checked={form.enabled.has(m)}
                        onChange={(e) => {
                          const n = new Set(form.enabled);
                          if (e.target.checked) n.add(m);
                          else n.delete(m);
                          setForm({ ...form, enabled: n });
                        }}
                      />
                      {PAYMENT_LABELS[m]}
                    </label>
                  ))}
                </div>
              </fieldset>
              {write && (
                <div className="flex gap-2">
                  <Button type="submit" disabled={save.busy}>
                    Simpan
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={test.busy}
                    onClick={() => void test.run()}
                  >
                    Uji koneksi
                  </Button>
                </div>
              )}
            </form>
          )}
        </Card>
        <Card title="Notifikasi (webhook)">
          <p className="text-sm text-slate-600">
            Isi <b>Payment Notification URL</b> di dashboard Midtrans (Settings → Configuration)
            dengan:
          </p>
          <code className="mt-2 block break-all rounded bg-slate-100 p-2 text-xs">{webhook}</code>
          <p className="mt-3 text-sm text-slate-600">
            Alamat harus bisa dijangkau dari internet (bukan localhost). Tanpa webhook, kasir tetap
            menerima status lewat pengecekan berkala setiap 3 detik. Server key disimpan terenkripsi
            dan tidak pernah ditampilkan kembali.
          </p>
          <p className="mt-3 text-sm text-slate-600">
            Pembayaran berlaku 15 menit. Refund non-tunai belum didukung di aplikasi (lakukan lewat
            dashboard Midtrans).
          </p>
        </Card>
      </div>
      <div className="mt-4">
        <Card
          title="Daftar pembayaran"
          actions={
            <div className="flex items-center gap-2">
              <div className="w-40">
                <SelectField
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Semua</option>
                  {Object.entries(statusText).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </SelectField>
              </div>
              <Button
                variant="secondary"
                disabled={reconcile.busy}
                onClick={() => void reconcile.run()}
              >
                Rekonsiliasi 24 jam
              </Button>
            </div>
          }
        >
          {recon && (
            <div className="mb-3 rounded bg-slate-50 p-3 text-sm">
              Diperiksa {recon.checked} · cocok {recon.matched} · disembuhkan {recon.healed.length}{' '}
              · selisih {recon.mismatched.length} · tak terjangkau {recon.unreachable} · order belum
              terbentuk {recon.missingOrder.length}
              {recon.mismatched.map((m) => (
                <div key={m.id} className="text-red-700">
                  {m.id.slice(-8)}: lokal {m.local} ≠ Midtrans {m.midtrans}
                </div>
              ))}
            </div>
          )}
          <DataTable columns={cols} rows={intents.data ?? []} />
        </Card>
      </div>
    </>
  );
}
